import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { apiError } from '../../services/api';
import CountrySelect from '../../components/ui/CountrySelect';
import { Card, Field, FormActions, Notice, PageHeader } from '../../components/ui/Page';

export default function CompanyFormPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ logicalName: '', name: '', email: '', phone: '', countryCode: 'IN', sector: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const { data } = await api.post('/admin/companies', form);
      navigate('/companies', { replace: true, state: { emailSent: data.emailSent } });
    } catch (err) {
      setError(apiError(err, 'Could not add the company.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <PageHeader crumbs={[{ label: 'Companies', to: '/companies' }, { label: 'New company' }]} title="Add company" description="Micus emails the contact an invite to complete their workspace. New companies are assigned to one@lab63.org by default." />
      <Card>
        <form onSubmit={onSubmit} className="grid gap-4 sm:grid-cols-2">
          <Field label="Micus company name" htmlFor="c-logical" className="sm:col-span-2" hint="Unique display name shown across Micus instead of the legal company name.">
            <input
              id="c-logical"
              required
              minLength={2}
              maxLength={80}
              pattern="[A-Za-z0-9][A-Za-z0-9 ._-]{1,79}"
              className="mz-input"
              value={form.logicalName}
              onChange={set('logicalName')}
              placeholder="e.g. Acme Working Capital"
              autoComplete="off"
            />
          </Field>
          <Field label="Legal / operating company name" htmlFor="c-name" className="sm:col-span-2" hint="Real company name — kept for documents, rating and invoices.">
            <input id="c-name" required minLength={2} className="mz-input" value={form.name} onChange={set('name')} placeholder="Acme Logistics Pvt Ltd" />
          </Field>
          <Field label="Contact email" htmlFor="c-email" hint="The invite goes here. The customer signs in with this email.">
            <input id="c-email" required type="email" className="mz-input" value={form.email} onChange={set('email')} placeholder="founder@acme.com" />
          </Field>
          <Field label="Phone" htmlFor="c-phone">
            <input id="c-phone" required minLength={5} className="mz-input" value={form.phone} onChange={set('phone')} placeholder="+91 98765 43210" />
          </Field>
          <Field label="Country" htmlFor="c-country" hint="Decides the document checklist, rating prompt and fees.">
            <CountrySelect id="c-country" value={form.countryCode} onChange={(code) => setForm((f) => ({ ...f, countryCode: code }))} />
          </Field>
          <Field label="Sector" htmlFor="c-sector" hint="Optional">
            <input id="c-sector" className="mz-input" value={form.sector} onChange={set('sector')} placeholder="Logistics" />
          </Field>
          {error && (
            <Notice tone="error" className="sm:col-span-2">
              {error}
            </Notice>
          )}
          <div className="sm:col-span-2">
            <FormActions>
              <button type="button" className="mz-btn-ghost" onClick={() => navigate('/companies')}>
                Cancel
              </button>
              <button type="submit" disabled={saving} className="mz-btn-primary">
                {saving ? 'Sending invite…' : 'Add company & send invite'}
              </button>
            </FormActions>
          </div>
        </form>
      </Card>
    </div>
  );
}
